import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * GoogleAnalytics Component
 * Loads Google Analytics 4 (gtag.js) dynamically based on backend configuration.
 * Respects GDPR cookie consent if configured.
 */
export default function GoogleAnalytics() {
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Load analytics configuration from backend
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/config`);
        if (response.ok) {
          const data = await response.json();
          if (data.enabled && data.measurement_id) {
            setConfig(data);
          }
        }
      } catch (error) {
        console.log('Analytics not configured');
      }
    };
    loadConfig();
  }, []);

  // Load GA script when config is ready and consent is given
  useEffect(() => {
    if (!config || loaded) return;

    // Check if cookie consent is required
    if (config.cookie_consent_required) {
      // Check if user has given consent (stored in localStorage by CookieConsent component)
      const cookieConsent = localStorage.getItem('rarifex_cookie_consent');
      if (!cookieConsent) {
        // Wait for consent
        const handleConsentChange = () => {
          const consent = localStorage.getItem('rarifex_cookie_consent');
          if (consent) {
            const parsed = JSON.parse(consent);
            if (parsed.analytics) {
              loadGoogleAnalytics();
            }
          }
        };

        // Listen for consent changes
        window.addEventListener('storage', handleConsentChange);
        
        // Also check on component mount in case consent was already given
        handleConsentChange();

        return () => {
          window.removeEventListener('storage', handleConsentChange);
        };
      } else {
        const parsed = JSON.parse(cookieConsent);
        if (!parsed.analytics) {
          // User declined analytics cookies
          return;
        }
      }
    }

    // Load GA
    loadGoogleAnalytics();

  }, [config, loaded]);

  const loadGoogleAnalytics = () => {
    if (loaded || !config?.measurement_id) return;

    // Create and load the gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${config.measurement_id}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    gtag('js', new Date());

    // Configure with options
    const configOptions = {};
    if (config.anonymize_ip) {
      configOptions.anonymize_ip = true;
    }

    gtag('config', config.measurement_id, configOptions);

    setLoaded(true);
    console.log('Google Analytics loaded:', config.measurement_id);
  };

  // This component doesn't render anything
  return null;
}

/**
 * Track a page view manually (for SPA navigation)
 * Call this from your router or page components
 */
export function trackPageView(pagePath, pageTitle) {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
}

/**
 * Track a custom event
 */
export function trackEvent(eventName, eventParams = {}) {
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

/**
 * Track e-commerce events
 */
export const ecommerce = {
  // Track when user views a product
  viewItem: (item) => {
    trackEvent('view_item', {
      currency: 'EUR',
      value: item.price,
      items: [{
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        price: item.price
      }]
    });
  },

  // Track when user adds to cart
  addToCart: (item, quantity = 1) => {
    trackEvent('add_to_cart', {
      currency: 'EUR',
      value: item.price * quantity,
      items: [{
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        price: item.price,
        quantity: quantity
      }]
    });
  },

  // Track when user starts checkout
  beginCheckout: (items, totalValue) => {
    trackEvent('begin_checkout', {
      currency: 'EUR',
      value: totalValue,
      items: items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity
      }))
    });
  },

  // Track completed purchase
  purchase: (transactionId, items, totalValue) => {
    trackEvent('purchase', {
      transaction_id: transactionId,
      currency: 'EUR',
      value: totalValue,
      items: items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity
      }))
    });
  }
};
